import { type Property } from "@/types/property";
import { useDashboardAggregates } from "./useDashboardAggregates";
import { useMemo } from "react";

interface UseDashboardMetricsProps {
  selectedYear: string;
  selectedMonth: string;
  selectedProperties: string[];
  selectedOrganizations: string[];
  properties: Property[];
}

const useDashboardMetrics = ({
  selectedYear,
  selectedMonth,
  selectedProperties,
  selectedOrganizations,
  properties,
}: UseDashboardMetricsProps) => {
  const year = parseInt(selectedYear);
  const month = parseInt(selectedMonth);

  // Fetch aggregated metrics from database function (fast, no pagination limits)
  const { data: aggregates, isLoading: isLoadingAggregates } = useDashboardAggregates({
    year,
    month,
    propertyIds: selectedProperties.length > 0 ? selectedProperties : undefined,
    organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
  });

  const metrics = useMemo(() => {
    if (!aggregates) {
      return null;
    }

    // Main metrics come directly from aggregates (accurate, no pagination issues)
    const receivedIncome = aggregates.received_income;
    const futureIncome = aggregates.future_income;
    const totalExpenses = aggregates.total_expenses;

    /**
     * ============================================================================
     * CÁLCULO DE TASA DE OCUPACIÓN
     * ============================================================================
     * 
     * Fórmula: (Días Reservados / Días Disponibles) × 100
     * 
     * Componentes:
     * ------------
     * 1. Días Reservados (total_reserved_days):
     *    - Suma de noches ocupadas por reservaciones en el mes
     *    - Se calcula como check_out - check_in para cada reservación
     *    - Si la reservación cruza límites del mes, se ajusta a los límites
     *    - IMPORTANTE: El día de check-out NO se cuenta como ocupado 
     *      (la noche termina ese día, la propiedad queda disponible)
     *    - NO incluye reservaciones canceladas
     * 
     * 2. Días Disponibles = Total Días Operativos - Días Bloqueados
     *    a) Total Días Operativos (total_possible_days):
     *       - Para cada propiedad: cuenta días desde MAX(operation_start_date, primer_día_mes)
     *       - Hasta el último día del mes
     *       - Si la propiedad inició operación después del fin del mes, contribuye 0 días
     *       - Esto corrige el cálculo para propiedades con inicio de operación durante el mes
     * 
     *    b) Días Bloqueados (total_blocked_days):
     *       - Días marcados manualmente como no disponibles
     *       - Incluye: mantenimiento, uso personal, bloqueos de iCal, etc.
     *       - Se RESTAN del denominador porque no estaban disponibles para rentar
     * 
     * Ejemplo Práctico: EC.09_PDS-12C en Noviembre 2025
     * --------------------------------------------------
     * - operation_start_date: 2025-11-11
     * - Días operativos: 30 - 11 + 1 = 20 días (del 11 al 30 de noviembre)
     * - Días bloqueados: 1
     * - Días disponibles: 20 - 1 = 19 días
     * - Días reservados: 15 días
     * - Tasa de ocupación: (15 / 19) × 100 = 78.95%
     * 
     * Ejemplo cuando la propiedad NO operaba: EC.09_PDS-12C en Octubre 2025
     * ----------------------------------------------------------------------
     * - operation_start_date: 2025-11-11 (después del mes consultado)
     * - Días operativos: 0 días
     * - Ocupación: 0% (la propiedad no contribuye al cálculo)
     * 
     * Ejemplo con múltiples propiedades en Noviembre 2025:
     * -----------------------------------------------------
     * - EC.09_PDS-12C: operation_start_date = 2025-11-11 → 20 días (11-30)
     * - EC.08_PLH-158: operation_start_date = 2025-11-01 → 30 días (1-30)
     * - Total días operativos: 20 + 30 = 50 días
     * - Días bloqueados (ambas propiedades): 2
     * - Días disponibles: 50 - 2 = 48 días
     * 
     * Consideraciones Importantes:
     * ----------------------------
     * - Si availableDays = 0 (todos los días bloqueados o propiedad no operaba), la tasa es 0%
     * - Las reservaciones que cruzan meses solo cuentan los días dentro del mes
     * - Los días bloqueados se excluyen del cálculo para reflejar días 
     *   realmente disponibles para rentar
     * - El resultado se redondea a 2 decimales para visualización
     * ============================================================================
     */
    const totalBlockedDays = aggregates.total_blocked_days;
    const totalPossibleDays = aggregates.total_possible_days;
    const availableDays = Math.max(0, totalPossibleDays - totalBlockedDays);

    const occupancyRate =
      availableDays > 0 ? (aggregates.total_reserved_days / availableDays) * 100 : 0;

    /**
     * ============================================================================
     * CÁLCULO DE COSTO POR NOCHE PROMEDIO
     * ============================================================================
     * 
     * Fórmula: Ingresos Recibidos / Días Reservados (solo pagados)
     * 
     * Componentes:
     * ------------
     * 1. Ingresos Recibidos (receivedIncome):
     *    - Suma de property_income.payout para el mes
     *    - Se basa en payout_date (fecha de pago), NO en las fechas de la reservación
     *    - Incluye todos los tipos de ingreso: Hospedaje, Early Checkin, 
     *      Late Checkout, Limpieza adicional, etc.
     *    - Representa el dinero efectivamente recibido por la propiedad
     * 
     * 2. Días Reservados (solo pagados) (total_reserved_days_paid_only):
     *    - Suma de noches ocupadas SOLO de reservaciones donde paid = true
     *    - Se calcula como check_out - check_in para cada reservación pagada
     *    - Si la reservación cruza límites del mes, se ajusta a los límites
     *    - IMPORTANTE: El día de check-out NO se cuenta como ocupado
     *    - NO incluye reservaciones no pagadas (paid = false)
     *    - NO incluye reservaciones canceladas
     * 
     * Ejemplo Práctico: PDS-7E en Noviembre 2025
     * -------------------------------------------
     * - Ingresos Recibidos: $19,031.05
     *   • 5 pagos de hospedaje registrados en property_income
     *   • Incluye income por Late Checkout ($500)
     *   • Suma de todos los payout del mes
     * 
     * - Días Reservados (pagados): 13 días
     *   • 5 reservaciones con paid = true
     *   • Noches: 2 + 3 + 2 + 3 + 3 = 13 días
     *   • Solo cuenta días dentro de noviembre
     * 
     * - Costo por Noche Promedio: $19,031.05 / 13 = $1,464.00
     * 
     * Consideraciones Importantes:
     * ----------------------------
     * - Los ingresos se cuentan por payout_date, no por fechas de reservación
     * - Solo las reservaciones PAGADAS (paid = true) cuentan para los días
     * - El resultado representa el ingreso promedio por noche efectivamente ocupada
     * - Si no hay días reservados pagados, el costo promedio es 0
     * - Las reservaciones que cruzan meses solo cuentan los días dentro del mes
     * - Los ingresos adicionales (Early Checkin, Late Checkout) se incluyen
     *   en el numerador pero no afectan el conteo de días
     * - El resultado se redondea a 2 decimales para visualización
     * ============================================================================
     */
    const averageCostPerNight =
      aggregates.total_reserved_days_paid_only > 0
        ? receivedIncome / aggregates.total_reserved_days_paid_only
        : 0;

    /**
     * ============================================================================
     * CÁLCULO DE NOCHES POR RENTAR
     * ============================================================================
     * 
     * Fórmula: Contar días desde fecha_actual hasta fin_de_mes que estén libres
     * 
     * Componentes:
     * ------------
     * 1. Rango de fechas:
     *    - Inicio: MAX(fecha_actual, primer_día_del_mes)
     *    - Fin: último_día_del_mes (inclusive)
     * 
     * 2. Día disponible = NO bloqueado AND NO reservado
     *    - NO bloqueado: No existe en property_blocked_days
     *    - NO reservado: No hay booking donde check_in <= día < check_out
     * 
     * 3. Día de check-out:
     *    - Se considera DISPONIBLE (el huésped se va, la propiedad queda libre)
     * 
     * 4. Última noche del mes:
     *    - La noche del 30 de noviembre (check-in el 30) se cuenta como noche de noviembre
     * 
     * Ejemplo: PDS-7E en Noviembre 2025 (fecha actual: 26 nov)
     * ---------------------------------------------------------
     * - Días restantes del mes: 26, 27, 28, 29, 30
     * - Días reservados: 26 (reservación 27-29), 27, 28
     * - Días disponibles: 29 y 30
     * - Noches por rentar: 2
     * 
     * Consideraciones Importantes:
     * ----------------------------
     * - NO se cuentan noches pasadas (incluso del mismo mes)
     * - La fecha actual SÍ se cuenta si está libre
     * - Si hay múltiples propiedades, se suman todas las noches disponibles
     * - Solo considera propiedades seleccionadas en el filtro
     * ============================================================================
     */
    const nightsToRent = aggregates.nights_to_rent;

    /**
     * ============================================================================
     * CÁLCULO DE TASA DE OCUPACIÓN 7 DÍAS
     * ============================================================================
     * Igual que la tasa de ocupación mensual pero solo para los próximos 7 días
     */
    const occupancyRate7Days = aggregates.occupancy_rate_7_days;
    const availableDays7 = Math.max(
      0,
      occupancyRate7Days.possible_days - occupancyRate7Days.blocked_days
    );
    const rate7Days =
      availableDays7 > 0
        ? Math.round((occupancyRate7Days.reserved_days / availableDays7) * 100)
        : 0;

    /**
     * ============================================================================
     * CÁLCULO DE TASA DE OCUPACIÓN 15 DÍAS
     * ============================================================================
     * Igual que la tasa de ocupación mensual pero solo para los próximos 15 días
     */
    const occupancyRate15Days = aggregates.occupancy_rate_15_days;
    const availableDays15 = Math.max(
      0,
      occupancyRate15Days.possible_days - occupancyRate15Days.blocked_days
    );
    const rate15Days =
      availableDays15 > 0
        ? Math.round((occupancyRate15Days.reserved_days / availableDays15) * 100)
        : 0;

    // Calculate accumulated profit (simplified - returns zeros for now)
    // TODO: Create separate backend function for full year accumulated data
    const accumulatedProfit = Array.from({ length: 12 }, () => 0);

    return {
      futureIncome,
      receivedIncome,
      totalExpenses,
      occupancyRate: Math.round(occupancyRate * 100) / 100,
      averageCostPerNight: Math.round(averageCostPerNight * 100) / 100,
      nightsToRent,
      occupancyRate7Days: rate7Days,
      occupancyRate7DaysDateStart: occupancyRate7Days.date_start,
      occupancyRate7DaysDateEnd: occupancyRate7Days.date_end,
      occupancyRate15Days: rate15Days,
      occupancyRate15DaysDateStart: occupancyRate15Days.date_start,
      occupancyRate15DaysDateEnd: occupancyRate15Days.date_end,
      accumulatedProfit,
    };
  }, [aggregates]);

  return {
    metrics,
    isLoading: isLoadingAggregates,
  };
};

export default useDashboardMetrics;
